import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Surface, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import { formatGel } from '../../sellerMeta';
import type { ShiftFlowValue } from '../useShiftFlow';
import { ShiftFigureRow } from './ShiftFigureRow';

/**
 * The close as the server recorded it: expected cash, the count and the
 * difference from the closed shift, not the client's reveal. They differ when a
 * sale landed between the reveal and the close. Render-only — every value comes
 * from `useShiftFlow`.
 */
export const ShiftClosedStep: React.FC<ShiftFlowValue> = ({ closeSummary }) => {
    const { t } = useTranslation('pages');

    if (!closeSummary) {
        return null;
    }

    return (
        <div className="flex flex-col gap-4">
            <Typography color="muted" type="body-sm">
                {t('seller.shift.closedSummary')}
            </Typography>
            <Surface
                className="flex flex-col gap-2 p-3"
                {...getNodeTestIdProp('seller-shift-closed-summary')}
                variant="secondary"
            >
                <ShiftFigureRow
                    label={t('seller.shift.expected')}
                    testId="seller-shift-closed-expected"
                    value={formatGel(closeSummary.expected)}
                />
                <ShiftFigureRow
                    label={t('seller.shift.counted')}
                    testId="seller-shift-closed-counted"
                    value={formatGel(closeSummary.counted)}
                />
                <ShiftFigureRow
                    diffKind={closeSummary.diffKind}
                    label={t('seller.shift.diff')}
                    testId="seller-shift-closed-diff"
                    value={formatGel(closeSummary.difference)}
                />
            </Surface>
        </div>
    );
};
